import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';

import { Space } from '../../../vendor/my-spaces/models/space.model';
import { SpaceActionsMenu } from './space-actions-menu';
import { SpacesViewProps } from './spaces-as-list.component';
import { SurfaceList } from './surface-list';
import { DemoRoomBadge } from './demo-room-badge';

/**
 * The library as photos: rooms are recognised by their picture faster than by
 * their name. The photo opens the details; ⋯ holds the same actions as the list.
 * (The vendor portal's picker grid carries selection controls the library
 * doesn't use, so the admin page has its own.)
 */
export const SpacesGrid = ({ spaces, onView, onDelete, onOpenInManager, onToggleDemo }: SpacesViewProps) => {
    const { t } = useTranslation();

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="spaces-grid">
            {spaces.map((space: Space, index) => {
                const name = space.name || t('ADMIN_SPACES.UNNAMED');
                const meta = [
                    space.spaceType,
                    space.height || space.width ? t('ADMIN_SPACES.SIZE_CM', { height: space.height, width: space.width }) : null,
                ].filter(Boolean);

                return (
                    <Card key={space.id || index} className="gap-0 py-0" data-testid="space-card">
                        <button
                            type="button"
                            onClick={() => onView(space)}
                            className="group block aspect-4/3 w-full overflow-hidden bg-muted focus-visible:outline-none"
                            aria-label={t('ADMIN_SPACES.VIEW_DETAILS', { name })}
                        >
                            {space.thumbnailPath && (
                                <img
                                    src={space.thumbnailPath}
                                    alt=""
                                    loading="lazy"
                                    className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.02] group-focus-visible:scale-[1.02]"
                                />
                            )}
                        </button>
                        <div className="flex items-start justify-between gap-2 border-t p-3">
                            <div className="flex min-w-0 flex-col gap-1.5">
                                <div className="min-w-0">
                                    <p className="flex min-w-0 items-center gap-2 text-sm font-medium">
                                        <span className="truncate">{name}</span>
                                        {space.isDemoDefault && <DemoRoomBadge />}
                                    </p>
                                    {meta.length > 0 && (
                                        <p className="truncate text-xs text-muted-foreground tabular-nums">{meta.join(' · ')}</p>
                                    )}
                                </div>
                                <SurfaceList surfaces={space.surfaces} />
                            </div>
                            <SpaceActionsMenu space={space} name={name} onOpenInManager={onOpenInManager} onToggleDemo={onToggleDemo} onDelete={onDelete} />
                        </div>
                    </Card>
                );
            })}
        </div>
    );
};
